import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import {ConsultationsMyAppointmentsQuery} from '@progyny/generated/graphql'

dayjs.extend(utc)
dayjs.extend(timezone)

// Replace all functions with dayjs
interface AppointmentLike {
  scheduledTime?: string | null
  status?: string | null
}

/** Upcoming: SCHEDULED or RESCHEDULED with scheduledTime >= now. */
export const filterUpcomingAppointments = <T extends AppointmentLike>(
  appointments: (T | null)[]
): T[] => {
  const now = new Date()
  return appointments.filter((appointment): appointment is T => {
    if (!appointment) return false
    const status = (appointment.status ?? '').toUpperCase()
    return (
      !!appointment.scheduledTime &&
      new Date(appointment.scheduledTime) >= now &&
      (status === 'SCHEDULED' || status === 'RESCHEDULED')
    )
  })
}

/** Past: COMPLETED or NO_SHOW only. (CANCELLED and future SCHEDULED are excluded.) */
export const filterPastAppointments = <T extends AppointmentLike>(
  appointments: (T | null)[]
): T[] => {
  return appointments.filter((appointment): appointment is T => {
    if (!appointment) return false
    const status = (appointment.status ?? '').toUpperCase()
    return status === 'COMPLETED' || status === 'NO_SHOW'
  })
}

export const formatAppointmentDate = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export const formatAppointmentDayOfWeek = (
  dateString: string | null | undefined,
  locale: string = 'en-US'
): string => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString(locale, {
    weekday: 'long',
  })
}

export const formatAppointmentTime = (
  dateString: string | null | undefined,
  locale: string = 'en-US'
): string => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/** Formats a time range from start time and optional duration in minutes (default 60). */
export const formatAppointmentTimeRange = (
  dateString: string | null | undefined,
  durationMinutes: number = 60
): string => {
  if (!dateString) return ''
  const start = new Date(dateString)
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000)
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }
  return `${start.toLocaleTimeString('en-US', options)} – ${end.toLocaleTimeString('en-US', options)}`
}

export const formatLanguage = (language: string): string => {
  const languageName = new Intl.DisplayNames(['en-US'], {type: 'language'})
  return languageName.of(language) ?? ''
}

export const getAppointmentsBySlug = (
  slug: string,
  appointmentsData?: ConsultationsMyAppointmentsQuery
) => {
  if (!appointmentsData) return []

  const programAppointments =
    appointmentsData?.consultations?.myAppointments?.filter(
      (appointment) => appointment?.programEnrollment?.program?.slug === slug
    )

  return programAppointments ?? []
}

export const getConsultationsBySlug = (
  slug: string,
  appointmentsData?: ConsultationsMyAppointmentsQuery
) => {
  if (!appointmentsData) return []

  const consultationsBySlug =
    appointmentsData?.consultations?.myAppointments?.filter(
      (consultation) =>
        consultation?.consultationEnrollment?.consultation?.slug === slug
    )

  return consultationsBySlug ?? []
}
